"use client";
import { GroupHeader } from "src/app/components/ui/Headers";
import { useDotButton } from "../../design2/components/SimilarPosts2";
import Link from "next/link";
import React, { useCallback, useEffect } from "react";
import Pagination from "./Pagination";
import useEmblaCarousel from "embla-carousel-react";
import { MemberType } from "src/types";
import { PropertyCardSkeleton } from "../../design2/components/Skeletons/Design2PropertyCard";
import Button from "src/app/components/ui/Button";
import { useCompanyStaff } from "src/app/lib/useCompanyStaff";
import Image from "next/image";
import { generateSlugWithId } from "src/app/serverUtils";
import { VerifiedBadge } from "src/app/components/Icon";
import { useDirectors } from "src/app/lib/useCompanyDirector";

const Teams = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    slidesToScroll: "auto",
  });

  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);
  const { data, isLoading } = useCompanyStaff();

  const isDemoMode = process.env.NEXT_PUBLIC_DEMO_MODE === "true";

  const { data: directorsData } = useDirectors();

  const staff: MemberType[] = [
    ...(!isDemoMode && directorsData ? (directorsData as MemberType[]) : []),
    ...(data?.staff ? (data.staff as MemberType[]) : []),
  ];

  return (
    <section className="mb-8">
      <div className="wrapper">
        <GroupHeader
          max={1000}
          subheader="Meet the talented professionals behind our success, dedicated to providing you with exceptional service and expertise."
          header="The Team Driving Our Vision"
        />
      </div>

      {isLoading ? (
        <div className="wrapper overflow-hidden">
          <div className="flex overflow-scroll">
            {Array.from({ length: 4 }, (_, index) => (
              <div
                key={index}
                className="pr-8 w-[300px] xs:w-[60%] xs:pr-2 shrink-0"
              >
                <PropertyCardSkeleton />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="wrapper">
            <div ref={emblaRef} className="overflow-hidden max-w-full">
              <div className="flex">
                {staff && staff.length > 0 ? (
                  staff.map((item, index) => (
                    <div
                      className="pr-12 w-[300px] xs:w-[75%] xs:pr-2 shrink-0"
                      key={index}
                    >
                      <Card {...item} />
                    </div>
                  ))
                ) : (
                  <div className="opacity-0 mb-4">
                    Lorem ipsum, dolor sit amet consectetur adipisicing elit.
                    Autem, accusamus.
                  </div>
                )}
              </div>
            </div>

            <div className="pagination">
              <Pagination
                scrollSnaps={scrollSnaps}
                selectedIndex={selectedIndex}
                onDotButtonClick={onDotButtonClick}
                emblaApi={emblaApi}
                variant
              />
            </div>
          </div>
        </>
      )}
    </section>
  );
};
export default Teams;

export const Card: React.FC<MemberType> = ({
  name,
  id,
  email,
  phone,
  image,
  role,
  is_verified,
}) => {
  const slugwithid = generateSlugWithId(Number(id), name);

  return (
    <div className="flex-col items-center text-secondary-main">
      <div className="imag overflow-hidden bg-themeColor-50 mx-auto size-[14rem] rounded-tr-[14rem] rounded-br-[14rem] rounded-tl-[14rem] relative">
        <Button
          href={`/staff/${slugwithid}`}
          className="absolute left-3 !bg-themeColor !opacity-1 !px-6 !py-2 whitespace-nowrap primary-btn bottom-4"
        >
          View Profile
        </Button>
        <Image
          height={300}
          width={300}
          className="object-cover block h-full w-full"
          src={image || "no image"}
          alt=""
        />
      </div>
      <div className="others w-max mx-auto">
        <h4 className="mt-3 h5 flex gap-2 items-center  line-clamp-1 max-w-[280px]">
          <span className="max-w-full block overflow-hidden truncate">
            {name}
          </span>{" "}
          <VerifiedBadge verified={is_verified as boolean} />
        </h4>
        <h5 className="text-themeColor h6 mb-3">{role}</h5>
        {email && (
          <div className="email mb-1 flex gap-2">
            <span className="block shrink-0">
              {" "}
              <EmailIcon />{" "}
            </span>
            <Link className="hover:underline" href={`mailto:${email}`}>
              <span className="xs:max-w-[200px] max-w-[220px] block overflow-hidden truncate">
                {email}
              </span>{" "}
            </Link>
          </div>
        )}

        {phone && (
          <div className="email flex gap-2">
            <PhoneIcon />{" "}
            <Link className="hover:underline" href={`tel:${phone}`}>
              {phone || "No phone available"}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

const EmailIcon = () => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M22 7.53516V17.0002C22 17.7654 21.7077 18.5017 21.1827 19.0584C20.6578 19.6152 19.9399 19.9503 19.176 19.9952L19 20.0002H5C4.23479 20.0002 3.49849 19.7078 2.94174 19.1829C2.38499 18.6579 2.04989 17.9401 2.005 17.1762L2 17.0002V7.53516L11.445 13.8322L11.561 13.8982C11.6977 13.965 11.8478 13.9997 12 13.9997C12.1522 13.9997 12.3023 13.965 12.439 13.8982L12.555 13.8322L22 7.53516Z"
        fill="var(--primary-color)"
      />
      <path
        d="M19.0003 4C20.0803 4 21.0273 4.57 21.5553 5.427L12.0003 11.797L2.44531 5.427C2.6961 5.01982 3.0406 4.6785 3.45008 4.43149C3.85957 4.18448 4.32217 4.03894 4.79931 4.007L5.00031 4H19.0003Z"
        fill="var(--primary-color)"
      />
    </svg>
  );
};

const PhoneIcon = () => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M16.5569 12.906L16.1019 13.359C16.1019 13.359 15.0189 14.435 12.0639 11.497C9.10886 8.55898 10.1919 7.48298 10.1919 7.48298L10.4779 7.19698C11.1849 6.49498 11.2519 5.36698 10.6349 4.54298L9.37486 2.85998C8.61086 1.83998 7.13586 1.70498 6.26086 2.57498L4.69086 4.13498C4.25786 4.56698 3.96786 5.12498 4.00286 5.74498C4.09286 7.33198 4.81086 10.745 8.81486 14.727C13.0619 18.949 17.0469 19.117 18.6759 18.965C19.1919 18.917 19.6399 18.655 20.0009 18.295L21.4209 16.883C22.3809 15.93 22.1109 14.295 20.8829 13.628L18.9729 12.589C18.1669 12.152 17.1869 12.28 16.5569 12.906Z"
        fill="var(--primary-color)"
      />
    </svg>
  );
};
